import {groupBy} from '@benchling/js-utils';
import gql from 'graphql-tag';

import {pluralize} from 'BenchlingUtil';
import {ProcessPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {StructuredTableEditStatus} from 'notebook/structured-tables/action-functions/toggle-edit-mode/reduceToggledTableEditState';
import * as StructuredTableTogglableStateActions from 'notebook/structured-tables/action-functions/toggle-edit-mode/StructuredTableTogglableStateActions';
import {
  AxisContextMenuActionFunctionParameters,
  StructuredTableContextMenuItem,
} from 'notebook/structured-tables/StructuredTableConstants';
import rowHasUnsubmittedChanges from 'notebook/structured-tables/util/rowHasUnsubmittedChanges';
import {SelectionRange, TableDimension} from 'notebook/tables/TableConstants';
import getDimensionalOperationText from 'notebook/tables/util/getDimensionalOperationText';
import CopyTasksConfirmationModal from 'processes/modals/CopyTasksConfirmationModal';
import {updateProcessDetailsQueryCache} from 'processes/process-details/ProcessDetailsQueryUtils';
import {ProcessTableLocation} from 'processes/ProcessConstants';
import {getProcessPlatformFeatureConfig} from 'processes/processes-platform/FeatureOptions';
import {
  ProcessTaskAssigneeQuery,
  ProcessTaskAssigneeQueryVariables,
  ProcessTaskAssigneeQuery_processTasks_ProcessTaskNode as ProcessTaskNode,
  ProcessTaskAssigneeQuery_processTasks_ProcessTaskNode_taskSchema_ProcessTaskSchemaNode as ProcessTaskSchemaNode,
} from 'processes/tables/action-functions/__generated/ProcessTaskAssigneeQuery';
import runModal from 'ui-library/runModal';
import APIUtil from 'util/APIUtil';
import {withLoadingScreen} from 'util/Promises';

import * as ProcessManagementActions from '../../ProcessManagementActions';
import ProcessTaskStructuredTableAdapter, {
  getTaskSchema,
  ProcessTaskStructuredTableMetadata,
  ProcessTaskStructuredTableRowMetadata,
} from '../ProcessTaskStructuredTableAdapter';

export const PROCESS_TASK_ASSIGNEE_QUERY = gql`
  query ProcessTaskAssigneeQuery($processTaskIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTasks(ids: $processTaskIds) {
      ... on ProcessTaskNode {
        taskSchema {
          ... on ProcessTaskSchemaNode {
            canSetAssigneeOnTaskCreation
            id
          }
        }
        assignee {
          ... on PrincipalInterface {
            id
          }
        }
        id
      }
    }
  }
`;

export async function copyTasks({
  entryOrTemplateId,
  structuredTableAdapter,
  structuredTable,
  taskIds,
  updateHandler,
}: AxisContextMenuActionFunctionParameters<
  ProcessTaskStructuredTableMetadata,
  ProcessTaskStructuredTableRowMetadata
> & {taskIds: Array<string>}): Promise<void> {
  const newTasks = await ProcessManagementActions.copyTasks(taskIds);
  if (newTasks == null || newTasks.length === 0) {
    return;
  }
  if (entryOrTemplateId != null) {
    return;
  }

  if (structuredTable.metadata.tableLocation === ProcessTableLocation.WORKSPACE_TAB) {
    // The workspace tab's task table is the only table in which all tasks exist together. The execution entry task table,
    // direct execution modal task table, etc only contain the tasks that have been executed. So since the new
    // tasks are created in a pending state we only need to update the workspace tab table.
    const updatedCells = [];
    const lsuvsByRow = [];
    const isFinalizedByRow = [];
    for (const {cells, lastSyncedUpstreamValues, isFinalized} of structuredTable.rows) {
      updatedCells.push(cells);
      lsuvsByRow.push(lastSyncedUpstreamValues ?? []);
      isFinalizedByRow.push(isFinalized);
    }
    let taskIndex = updatedCells.length;
    for (const task of newTasks) {
      updatedCells.push(
        {
          ...structuredTableAdapter.getBlankRow(structuredTable.metadata, taskIndex, structuredTable.columns),
          ...ProcessTaskStructuredTableAdapter.makeRowFromTask(task, structuredTable.columns),
        }.cells
      );
      lsuvsByRow.push(
        ProcessTaskStructuredTableAdapter.optimisticallyMakeLastSyncedUpstreamValuesFromTask(
          task,
          structuredTable.columns
        )
      );
      isFinalizedByRow.push(true);
      taskIndex++;
    }

    updateHandler({
      updatedCells,
      columnUpdates: [],
      lastSyncedUpstreamValuesByRow: lsuvsByRow,
      isFinalizedByRow,
    });
  }

  // Optimistically update any cached data for the workspace tab. This no-ops if the workspace tab data is not
  // already in the cache.
  const newTasksByProcessId = groupBy(newTasks, (task) => task.process.id);
  for (const [processId, newTasksForProcess] of Object.entries(newTasksByProcessId)) {
    updateProcessDetailsQueryCache(processId, {
      tasks: newTasksForProcess,
      mergeTasks: true,
    });
  }
}

function getRowsInRange<T>(rows: Array<T>, selectionRange: SelectionRange): Array<T> {
  return rows.slice(selectionRange.minIndex, selectionRange.maxIndex + 1);
}

function shouldShowCopyTaskActionFunction({
  entryOrTemplateId,
  structuredTableAdapter,
  structuredTable,
  computedTable,
  selectionRange,
  locked,
}: AxisContextMenuActionFunctionParameters<
  ProcessTaskStructuredTableMetadata,
  ProcessTaskStructuredTableRowMetadata
> & {structuredTableAdapter: ProcessTaskStructuredTableAdapter}): boolean {
  if (selectionRange == null || locked) {
    return false;
  }
  if (
    StructuredTableTogglableStateActions.getStructuredTableTogglableStatus(structuredTable.cid) ===
    StructuredTableEditStatus.EDITING
  ) {
    // Cannot duplicate tasks while a table with toggle-able edit functionality is in edit mode since that breaks
    // the table submission flow
    return false;
  }
  if (entryOrTemplateId != null && !APIUtil.isBaseEntry(entryOrTemplateId)) {
    // Cannot duplicate tasks in an entry template
    return false;
  }
  if (structuredTable.metadata.tableLocation === ProcessTableLocation.REQUEST_V2_DOCUMENT) {
    // Cannot duplicate tasks in a request document
    return false;
  }

  const taskSchema = getTaskSchema(structuredTable.schemas);
  if (
    taskSchema != null &&
    !getProcessPlatformFeatureConfig(
      ProcessPlatformFeatureType.CAN_COPY_TASKS,
      taskSchema.executionType,
      taskSchema.name
    )
  ) {
    return false;
  }

  const selectedRows = getRowsInRange(structuredTable.rows, selectionRange);
  const selectedComputedRows = getRowsInRange(computedTable, selectionRange);

  const tableLocationsToCheckUnsubmittedRows = [
    ProcessTableLocation.ENTRY,
    ProcessTableLocation.REQUEST_V2_DOCUMENT,
  ];

  if (
    structuredTable.metadata.tableLocation != null &&
    tableLocationsToCheckUnsubmittedRows.includes(structuredTable.metadata.tableLocation) &&
    selectedRows.some((row, idx) =>
      rowHasUnsubmittedChanges(
        selectedComputedRows[idx],
        row,
        structuredTable.columns,
        structuredTable.schemas
      )
    )
  ) {
    return false;
  }

  const taskIds = structuredTableAdapter.getTaskIdsFromRows(
    structuredTable.columns,
    selectedRows,
    selectedComputedRows
  );
  if (selectedRows.length !== taskIds.length) {
    // Cannot duplicate tasks since at least one row in the selection does not have a valid task to duplicate.
    return false;
  }

  return true;
}

export async function getWillRemoveAssigneeFromTask(taskIds: Array<string>): Promise<boolean> {
  const response = await doQuerySafe<ProcessTaskAssigneeQuery, ProcessTaskAssigneeQueryVariables>(
    {query: PROCESS_TASK_ASSIGNEE_QUERY},
    {variables: {processTaskIds: taskIds}}
  );
  const processTasks = response.processTasks;
  return processTasks.some(
    (processTask) =>
      (processTask as ProcessTaskNode).assignee != null &&
      ((processTask as ProcessTaskNode).taskSchema as ProcessTaskSchemaNode).canSetAssigneeOnTaskCreation ===
        false
  );
}

export function getCopyTaskActionFunction(
  params: AxisContextMenuActionFunctionParameters<
    ProcessTaskStructuredTableMetadata,
    ProcessTaskStructuredTableRowMetadata
  >
): StructuredTableContextMenuItem | null {
  const processTaskStructuredTableAdapter =
    params.structuredTableAdapter as ProcessTaskStructuredTableAdapter;
  if (
    !shouldShowCopyTaskActionFunction({...params, structuredTableAdapter: processTaskStructuredTableAdapter})
  ) {
    return null;
  }

  const taskIds = processTaskStructuredTableAdapter.getTaskIdsFromRows(
    params.structuredTable.columns,
    getRowsInRange(params.structuredTable.rows, params.selectionRange!),
    getRowsInRange(params.computedTable, params.selectionRange!)
  );

  return {
    name: getDimensionalOperationText({
      verb: `Create new ${pluralize(taskIds.length, 'task')} from`,
      selectionRange: params.selectionRange,
      dimension: TableDimension.ROW,
    }),
    onSelect: async () => {
      if (await getWillRemoveAssigneeFromTask(taskIds)) {
        await runModal(CopyTasksConfirmationModal, {taskIds});
        return;
      }
      await withLoadingScreen(copyTasks({...params, taskIds}), 'Creating new tasks');
    },
  };
}

export default {getRowAxisContextMenuItem: getCopyTaskActionFunction};
